import { PageContextEnvelope } from './types'

const MAX_HELP_TEXT_LENGTH = 120
const MAX_OPTIONS_PREVIEW = 5
const MAX_ERRORS = 20

const truncate = (text: string, max: number): string =>
  text.length > max ? `${text.slice(0, max)}…` : text

const serializeField = (field: {
  label: string
  type: string
  required: boolean
  helpText?: string
  enabled: boolean
  optionsPreview?: string[]
}): string => {
  const parts: string[] = []

  const attrs: string[] = [field.type]
  if (field.required) {
    attrs.push('required')
  }
  if (!field.enabled) {
    attrs.push('disabled')
  }
  parts.push(`- ${field.label} [${attrs.join(', ')}]`)

  if (field.optionsPreview?.length) {
    const preview = field.optionsPreview.slice(0, MAX_OPTIONS_PREVIEW)
    const suffix = field.optionsPreview.length > MAX_OPTIONS_PREVIEW ? ', …' : ''
    parts.push(` options: ${preview.join(', ')}${suffix}`)
  }

  if (field.helpText) {
    parts.push(` — ${truncate(field.helpText, MAX_HELP_TEXT_LENGTH)}`)
  }

  return parts.join('')
}

/**
 * Converts a PageContextEnvelope into a compact, LLM-friendly text format.
 * This text is injected into the system prompt so the AI can reason about
 * the page the citizen is viewing without receiving any PII.
 */
export const serializePageContext = (envelope: PageContextEnvelope): string => {
  const lines: string[] = []

  lines.push(`# ${envelope.page.pageTitle}`)
  lines.push(`PageType: ${envelope.page.pageType}  Route: ${envelope.page.routeName}`)

  const contextParts: string[] = []
  if (envelope.uiState.stepIndex != null && envelope.uiState.stepCount != null) {
    contextParts.push(`Step ${envelope.uiState.stepIndex + 1}/${envelope.uiState.stepCount}`)
  }
  if (envelope.uiState.activeStep) {
    contextParts.push(`Step: ${envelope.uiState.activeStep}`)
  }
  if (envelope.uiState.activeTab) {
    contextParts.push(`Tab: ${envelope.uiState.activeTab}`)
  }
  if (envelope.page.entityIds.recordId) {
    contextParts.push(`Record: ${envelope.page.entityIds.recordId}`)
  }
  if (contextParts.length > 0) {
    lines.push(`Context: ${contextParts.join(', ')}`)
  }

  if (envelope.visibleSchema.actions.length > 0) {
    lines.push('')
    lines.push('Actions:')
    for (const action of envelope.visibleSchema.actions) {
      const status = action.enabled ? 'enabled' : 'disabled'
      const reason = action.reasonDisabled ? ` (${action.reasonDisabled})` : ''
      lines.push(`- ${action.actionKey} [${status}]${reason}`)
    }
  }

  const errors = envelope.uiState.blockingErrors?.slice(0, MAX_ERRORS) ?? []
  if (errors.length > 0) {
    lines.push('')
    lines.push('Errors:')
    for (const error of errors) {
      lines.push(`- ${error.fieldKey}: ${error.message}`)
    }
    if ((envelope.uiState.blockingErrors?.length ?? 0) > MAX_ERRORS) {
      lines.push(`  … and ${(envelope.uiState.blockingErrors?.length ?? 0) - MAX_ERRORS} more`)
    }
  }

  const banners = envelope.uiState.banners ?? []
  if (banners.length > 0) {
    lines.push('')
    lines.push('Notices:')
    for (const banner of banners) {
      lines.push(`- [${banner.severity}] ${banner.message}`)
    }
  }

  for (const section of envelope.visibleSchema.sections) {
    lines.push('')
    lines.push(`## ${section.title}`)
    for (const field of section.fields) {
      lines.push(serializeField(field))
    }
  }

  const result = lines.join('\n')

  const TRUNCATION_SUFFIX = '\n[truncated]'
  if (result.length > envelope.budgets.maxChars) {
    return result.slice(0, envelope.budgets.maxChars - TRUNCATION_SUFFIX.length) + TRUNCATION_SUFFIX
  }

  return result
}
